// virtualCameraDetector — flag candidates using a virtual camera
// (feature_key: `lockdown.virtual_camera_detect`).
//
// Virtual cameras (OBS Virtual Camera, ManyCam, Snap Camera, XSplit
// Vcam, NVIDIA Broadcast, etc.) let someone pipe a pre-recorded video
// or another person's face into what looks like a webcam to the browser.
// Used to defeat face-presence and identity-match detection.
//
// Detection strategy: enumerate media devices and pattern-match the
// device labels against known virtual camera names. The label is
// populated after the user has granted camera permission (which they
// already have by the time the proctoring engine runs).
//
// Limitations:
//   * Easily defeated by renaming the virtual device. Some platforms
//     allow setting the camera label freely.
//   * Doesn't detect "screen mirror to webcam" tricks via OS APIs.
//   * Doesn't detect pre-recorded video being played into a real webcam.
//
// Still catches the ~80% of candidates who use the default install
// (OBS in particular: most users don't bother to rename the virtual cam).
//
// One-shot check at start, plus a re-check every 30s in case the user
// activates a virtual camera mid-test (e.g., switching the WebRTC source
// without restarting the test).

const FEATURE_VIRTUAL_CAMERA = 'lockdown.virtual_camera_detect';

// Patterns matched (case-insensitive) against MediaDeviceInfo.label.
// Conservative — only well-known virtual-camera software. Adding too many
// patterns risks false-positives on legitimate hardware (e.g. some
// laptop cams have "virtual" in their model name).
const VIRTUAL_CAMERA_PATTERNS = [
  /\bOBS\s+Virtual/i,
  /\bOBS\s+Camera/i,
  /ManyCam/i,
  /\bSnap\s+Camera/i,
  /Snap\s+Inc/i,
  /XSplit/i,
  /vMix/i,
  /Streamlabs/i,
  /ChromaCam/i,
  /NVIDIA\s+Broadcast/i,
  /Elgato\s+(Cam|Stream)/i,
  /\bv4l2loopback/i,
  /\bvirtual\s+camera/i,
  /\bvirtual\s+webcam/i,
  /\bvCam\b/i,
];


const num = (obj, key, fallback) => {
  const v = obj?.[key];
  if (v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};


/**
 * Returns the list of detected virtual camera device labels, or [].
 * Safe to call without camera permission — labels will simply be empty
 * strings and nothing will match.
 */
async function listVirtualCameras() {
  try {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return [];
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter((d) => d.kind === 'videoinput');
    return videoInputs
      .filter((d) => VIRTUAL_CAMERA_PATTERNS.some((p) => p.test(d.label || '')))
      .map((d) => d.label || '(unknown)');
  } catch (_) {
    return [];
  }
}


export function createVirtualCameraDetector({
  emit,
  getFlag,
  getThreshold,
}) {
  if (typeof emit !== 'function') {
    throw new Error('[virtualCameraDetector] emit must be a function');
  }

  let running = false;
  let intervalId = null;
  // Once we've reported a particular label, don't keep re-emitting it
  // every recheck — that would spam the event log. The set is keyed by
  // the matched label string.
  const reportedLabels = new Set();

  const runCheck = async () => {
    if (!running || !getFlag(FEATURE_VIRTUAL_CAMERA)) return;

    const matches = await listVirtualCameras();
    if (matches.length === 0) return;

    const newOnes = matches.filter((label) => !reportedLabels.has(label));
    if (newOnes.length === 0) return;

    newOnes.forEach((label) => reportedLabels.add(label));

    emit({
      feature_key: FEATURE_VIRTUAL_CAMERA,
      severity: 'critical',
      score_delta: 25,
      message: `Virtual camera detected (${newOnes.length} device${newOnes.length > 1 ? 's' : ''})`,
      evidence: {
        stage: 'flag',
        matched_labels: newOnes,
        all_matched_so_far: Array.from(reportedLabels),
      },
    });
  };

  const start = async () => {
    if (running) return;
    running = true;
    // Initial check immediately on start. Then re-check at the configured
    // interval — catches users who activate a virtual camera mid-test.
    await runCheck();
    const thr = getThreshold(FEATURE_VIRTUAL_CAMERA);
    const recheckSeconds = num(thr, 'recheck_seconds', 30);
    intervalId = setInterval(() => { void runCheck(); }, recheckSeconds * 1000);
  };

  const stop = () => {
    running = false;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    reportedLabels.clear();
  };

  return { start, stop, isReady: () => running };
}
